import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  GripVertical,
  Eye,
  EyeOff,
  ExternalLink,
  Image,
} from 'lucide-react';

const CmsFormations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formations, setFormations] = useState([]);
  const [editingFormation, setEditingFormation] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const defaultFormation = {
    title: '',
    slug: '',
    short_intro: '',
    description: '',
    learning_outcomes: [],
    collaborator: '',
    trainer_name: '',
    brochure_url: '',
    external_link: '',
    image_url: '',
    order: 0,
    is_featured: false,
    status: 'publie',
  };

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchFormations();
    fetchMedia();
  }, [user, navigate]);

  const fetchFormations = async () => {
    try {
      const response = await adminCmsAPI.listFormations();
      setFormations(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await adminCmsAPI.listMedia('formations');
      setMediaList(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const handleSave = async () => {
    if (!editingFormation.title || !editingFormation.slug) {
      toast.error('Le titre et le slug sont requis');
      return;
    }

    setSaving(true);
    try {
      if (editingFormation.id) {
        await adminCmsAPI.updateFormation(editingFormation.id, editingFormation);
        toast.success('Formation mise à jour');
      } else {
        await adminCmsAPI.createFormation(editingFormation);
        toast.success('Formation créée');
      }
      setShowDialog(false);
      setEditingFormation(null);
      fetchFormations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation?')) return;
    
    try {
      await adminCmsAPI.deleteFormation(id);
      toast.success('Formation supprimée');
      fetchFormations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (formation) => {
    try {
      const newStatus = formation.status === 'publie' ? 'brouillon' : 'publie';
      await adminCmsAPI.updateFormation(formation.id, { status: newStatus });
      toast.success(`Formation ${newStatus === 'publie' ? 'publiée' : 'mise en brouillon'}`);
      fetchFormations();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleOutcomesChange = (text) => {
    const outcomes = text.split('\n').filter(p => p.trim());
    setEditingFormation({ ...editingFormation, learning_outcomes: outcomes });
  };

  const selectImage = (url) => {
    setEditingFormation({ ...editingFormation, image_url: url });
    setShowMediaPicker(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-formations">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Formations</h1>
                <p className="text-white/60 text-sm">{formations.length} formation(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingFormation({ ...defaultFormation, order: formations.length });
                setShowDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle formation
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          {formations.map((formation, index) => (
            <Card key={formation.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="cursor-move text-neutral-400">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  {formation.image_url && (
                    <img 
                      src={formation.image_url} 
                      alt={formation.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#1D1D1B]">{formation.title}</h3>
                      <Badge variant={formation.status === 'publie' ? 'default' : 'secondary'}>
                        {formation.status === 'publie' ? 'Publié' : 'Brouillon'}
                      </Badge>
                      {formation.is_featured && (
                        <Badge variant="outline">Vedette</Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500 line-clamp-1">{formation.short_intro}</p>
                    {formation.collaborator && (
                      <p className="text-xs text-[#F9B438]">Partenaire : {formation.collaborator}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {formation.external_link && (
                      <a href={formation.external_link} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleStatus(formation)}
                    >
                      {formation.status === 'publie' ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-neutral-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingFormation(formation);
                        setShowDialog(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(formation.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {formations.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-neutral-500 mb-4">Aucune formation pour le moment</p>
                <Button 
                  onClick={() => {
                    setEditingFormation({ ...defaultFormation });
                    setShowDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer la première formation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFormation?.id ? 'Modifier la formation' : 'Nouvelle formation'}
            </DialogTitle>
          </DialogHeader>
          
          {editingFormation && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={editingFormation.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setEditingFormation({
                        ...editingFormation,
                        title,
                        slug: editingFormation.id ? editingFormation.slug : generateSlug(title),
                      });
                    }}
                    placeholder="Formation PCSWMM Niveau 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input
                    value={editingFormation.slug}
                    onChange={(e) => setEditingFormation({ ...editingFormation, slug: e.target.value })}
                    placeholder="formation-pcswmm-niveau-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Introduction courte</Label>
                <Textarea
                  value={editingFormation.short_intro}
                  onChange={(e) => setEditingFormation({ ...editingFormation, short_intro: e.target.value })}
                  placeholder="Résumé de la formation..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Description complète</Label>
                <Textarea
                  value={editingFormation.description}
                  onChange={(e) => setEditingFormation({ ...editingFormation, description: e.target.value })}
                  placeholder="Description détaillée..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Objectifs d'apprentissage (un par ligne)</Label>
                <Textarea
                  value={editingFormation.learning_outcomes?.join('\n') || ''}
                  onChange={(e) => handleOutcomesChange(e.target.value)}
                  placeholder="Comprendre les bases&#10;Maîtriser les outils&#10;Appliquer les concepts"
                  rows={5}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Partenaire / Collaborateur</Label>
                  <Input
                    value={editingFormation.collaborator || ''}
                    onChange={(e) => setEditingFormation({ ...editingFormation, collaborator: e.target.value })}
                    placeholder="Genium360"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Formateur</Label>
                  <Input
                    value={editingFormation.trainer_name || ''}
                    onChange={(e) => setEditingFormation({ ...editingFormation, trainer_name: e.target.value })}
                    placeholder="Bérenger Ypou, ing."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lien externe</Label>
                <Input
                  value={editingFormation.external_link || ''}
                  onChange={(e) => setEditingFormation({ ...editingFormation, external_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex gap-2">
                  <Input
                    value={editingFormation.image_url || ''}
                    onChange={(e) => setEditingFormation({ ...editingFormation, image_url: e.target.value })}
                    placeholder="URL de l'image"
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={() => setShowMediaPicker(true)}>
                    <Image className="w-4 h-4 mr-2" />
                    Choisir
                  </Button>
                </div>
                {editingFormation.image_url && (
                  <img 
                    src={editingFormation.image_url} 
                    alt="Aperçu"
                    className="w-32 h-24 object-cover rounded mt-2"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingFormation.status}
                    onChange={(e) => setEditingFormation({ ...editingFormation, status: e.target.value })}
                  >
                    <option value="publie">Publié</option>
                    <option value="brouillon">Brouillon</option>
                  </select>
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingFormation.is_featured}
                      onChange={(e) => setEditingFormation({ ...editingFormation, is_featured: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Mettre en vedette</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingFormation?.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choisir une image</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {mediaList.filter(m => m.file_type === 'image').map((media) => (
              <div
                key={media.id}
                className="aspect-square bg-neutral-100 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#F9B438] transition-all"
                onClick={() => selectImage(media.url)}
              >
                <img
                  src={media.url}
                  alt={media.alt_text || media.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {mediaList.filter(m => m.file_type === 'image').length === 0 && (
              <p className="col-span-4 text-center text-neutral-500 py-8">
                Aucune image disponible. Téléversez d'abord des images dans la médiathèque.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMediaPicker(false)}>
              Annuler
            </Button>
            <Link to="/cms/media">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Téléverser une image
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsFormations;
